<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <!-- Contenedor Principal del Modal (Ancho 7xl para acomodar Formulario Izquierda + Mapa Derecha) -->
    <div class="bg-white dark:bg-slate-900 w-full max-w-7xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <!-- Encabezado Estándar del Sistema -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Package class="w-5 h-5 text-[#F36421]" />
            {{ form.id ? 'Editar Solicitud de Envío' : 'Crear Solicitud de Envío / Cotizador LLEVO' }}
          </h2>
          <p class="text-xs text-slate-500">Cotizador conectado a libreta de sucursales (`customer_addresses`) y motor OSRM satelital.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Barra de Pestañas Horizontal Superior -->
      <div class="flex overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-[#F36421] text-[#F36421] bg-white dark:bg-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700']">
          {{ tab.name }}
        </button>
      </div>

      <!-- Cuerpo del Formulario: Grid 12 Columnas (5 Izquierda Formulario | 7 Derecha Mapa Satelital) -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="orderForm" @submit.prevent="handleSubmit" novalidate>
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">

            <!-- =============================================================== -->
            <!-- COLUMNA IZQUIERDA (5 COLS): FORMULARIO ACTIVO SEGÚN PESTAÑA -->
            <!-- =============================================================== -->
            <div class="lg:col-span-5 space-y-4">

              <!-- PESTAÑA 1: ORIGEN (PICKUP) -->
              <div v-show="activeTab === 'pickup'" class="space-y-4 animate-fade-in">
                <div :class="sectionCardClasses" class="space-y-4">
                  <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <h3 :class="[sectionTitleClasses, 'mb-0']">
                      <Store class="w-4 h-4 text-[#F36421]" />
                      Comercio & Punto Origen
                    </h3>
                    <span class="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                      🟢 Origen
                    </span>
                  </div>

                  <!-- 1. Selección del Cliente -->
                  <div>
                    <label :class="labelClasses">* Seleccionar Comercio / Cliente</label>
                    <select v-model="form.client_id" @change="onClientChange" :class="inputClasses" required>
                      <option value="" disabled>Selecciona el cliente del catálogo...</option>
                      <option v-for="client in clientsList" :key="client.id" :value="client.id">
                        {{ client.customer_type === 'B2B_BUSINESS' ? '🏢 ' : '👤 ' }}
                        {{ client.business_name || `${client.first_name} ${client.last_name}` }} ({{ client.mobile_phone }})
                      </option>
                    </select>
                  </div>

                  <!-- 2. LIBRETA DE SUCURSALES (customer_addresses) -->
                  <div>
                    <div class="flex justify-between items-center mb-1">
                      <label :class="[labelClasses, 'mb-0']">Sucursal / Dirección Guardada (`customer_addresses`)</label>
                      <span v-if="isLoadingAddresses" class="text-[10px] text-blue-500 font-bold flex items-center gap-1">
                        <Loader2 class="w-3 h-3 animate-spin" /> Cargando libreta...
                      </span>
                    </div>
                    <select
                      v-model="selectedPickupAddressId"
                      @change="applyPickupAddress"
                      :disabled="clientAddresses.length === 0 || isLoadingAddresses"
                      :class="inputClasses"
                    >
                      <option value="" disabled>
                        {{ clientAddresses.length > 0 ? 'Selecciona una sucursal de la libreta...' : 'Este cliente no tiene sucursales guardadas' }}
                      </option>
                      <option v-for="addr in clientAddresses" :key="addr.id" :value="addr.id">
                        {{ addr.is_default || addr.is_default === 1 ? '★ [Principal]' : '📍' }} {{ addr.alias }} — {{ addr.address }}
                      </option>
                    </select>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label :class="labelClasses">* Latitud Origen</label>
                      <input v-model.number="form.pickup_lat" type="number" step="0.00000001" :class="inputClasses" @change="calculateOSRMRoute" required />
                    </div>
                    <div>
                      <label :class="labelClasses">* Longitud Origen</label>
                      <input v-model.number="form.pickup_lng" type="number" step="0.00000001" :class="inputClasses" @change="calculateOSRMRoute" required />
                    </div>
                  </div>

                  <div>
                    <label :class="labelClasses">* Dirección / Señas de Recolección</label>
                    <input v-model="form.pickup_address" type="text" :class="inputClasses" placeholder="Ej: Burger King Altamira, Managua" required />
                  </div>

                  <button
                    type="button"
                    @click="activateMapMode('pickup')"
                    class="w-full py-2.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 border border-slate-300 dark:border-slate-600"
                  >
                    <MapPin class="w-4 h-4 text-emerald-600" />
                    📍 Clic en el Mapa para Fijar Origen
                  </button>
                </div>
              </div>

              <!-- PESTAÑA 2: DESTINO (DROPOFF) -->
              <div v-show="activeTab === 'dropoff'" class="space-y-4 animate-fade-in">
                <div :class="sectionCardClasses" class="space-y-4">
                  <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <h3 :class="[sectionTitleClasses, 'mb-0']">
                      <MapPin class="w-4 h-4 text-red-500" />
                      Destino & Receptor
                    </h3>
                    <span class="text-[10px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                      🔴 Entrega
                    </span>
                  </div>

                  <!-- OPCIONAL: ELEGIR DESTINO DESDE LA MISMA LIBRETA DEL CLIENTE -->
                  <div v-if="clientAddresses.length > 0">
                    <label :class="labelClasses">¿Entregar en otra dirección de su libreta? (Opcional)</label>
                    <select
                      v-model="selectedDropoffAddressId"
                      @change="applyDropoffAddress"
                      :class="inputClasses"
                    >
                      <option value="">-- Escribir dirección manualmente --</option>
                      <option v-for="addr in clientAddresses" :key="addr.id" :value="addr.id">
                        📍 {{ addr.alias }} — {{ addr.address }}
                      </option>
                    </select>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label :class="labelClasses">* Latitud Destino</label>
                      <input v-model.number="form.dropoff_lat" type="number" step="0.00000001" :class="inputClasses" @change="calculateOSRMRoute" required />
                    </div>
                    <div>
                      <label :class="labelClasses">* Longitud Destino</label>
                      <input v-model.number="form.dropoff_lng" type="number" step="0.00000001" :class="inputClasses" @change="calculateOSRMRoute" required />
                    </div>
                  </div>

                  <div>
                    <label :class="labelClasses">* Dirección / Señas de Entrega</label>
                    <input v-model="form.dropoff_address" type="text" :class="inputClasses" placeholder="Ej: De la Pepsi 2c al lago, casa color mamey..." required />
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label :class="labelClasses">Nombre Quien Recibe</label>
                      <input v-model="form.receiver_name" type="text" :class="inputClasses" placeholder="Nombre completo" />
                    </div>
                    <div>
                      <label :class="labelClasses">WhatsApp Receptor</label>
                      <input v-model="form.receiver_phone" type="text" :class="inputClasses" placeholder="8888-8888" />
                    </div>
                  </div>

                  <button
                    type="button"
                    @click="activateMapMode('dropoff')"
                    class="w-full py-2.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 border border-slate-300 dark:border-slate-600"
                  >
                    <MapPin class="w-4 h-4 text-red-600" />
                    📍 Clic en el Mapa para Fijar Destino
                  </button>
                </div>
              </div>

              <!-- PESTAÑA 3: TARIFA (`service_tiers`), COD & MAPA DE RUTA OSRM -->
              <div v-show="activeTab === 'pricing'" class="space-y-4 animate-fade-in">
                <div :class="sectionCardClasses" class="space-y-4">
                  <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <h3 :class="[sectionTitleClasses, 'mb-0']">
                      <Truck class="w-4 h-4 text-[#F36421]" />
                      Cotización & Reglas de Pago
                    </h3>
                    <span class="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-orange-100 text-[#F36421] dark:bg-orange-950/40">
                      Comisión: {{ getTierCommission(selectedTier) }}% LLEVO
                    </span>
                  </div>

                  <!-- SELECTOR DINÁMICO DE TARIFARIO (`service_tiers`) -->
                  <div>
                    <label :class="labelClasses">* Tarifa Logística (`service_tiers`)</label>
                    <select v-model="form.service_tier_id" :class="inputClasses" required>
                      <option value="" disabled>Selecciona el servicio configurado...</option>
                      <option v-for="tier in tiersList" :key="tier.id" :value="tier.id">
                        {{ getFleetIcon(tier.service_type) }} {{ tier.name }} (Base C$ {{ formatMoney(tier.base_fare) }} | KM Extra C$ {{ formatMoney(tier.price_per_km) }})
                      </option>
                    </select>
                  </div>

                  <!-- SELECTOR DE MÉTODO DE PAGO Y REGLAS DE NEGOCIO -->
                  <div>
                    <label :class="labelClasses">* Método de Pago & Regla Contable</label>
                    <select v-model="form.payment_method" :class="inputClasses" required>
                      <option value="CASH_ON_DELIVERY">💵 Cliente paga en efectivo (Genera CXP al negocio)</option>
                      <option value="PAID_TO_BUSINESS">💳 Cliente le pagó al negocio (Genera CXC al negocio)</option>
                      <option value="DIRECT_SHIPPING_PAID">🤝 Negocio paga directo el envío (Driver Split 75/25)</option>
                    </select>
                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                      <span v-if="form.payment_method === 'CASH_ON_DELIVERY'">
                        <b>Regla CXP:</b> El chofer cobra el efectivo del producto en destino. Generará saldo a favor (por pagar) al comercio.
                      </span>
                      <span v-else-if="form.payment_method === 'PAID_TO_BUSINESS'">
                        <b>Regla CXC:</b> El producto ya está pagado. LLEVO sumará el costo del flete a la cuenta por cobrar del comercio.
                      </span>
                      <span v-else>
                        <b>Regla Directa:</b> El motorizado cobró o recibió el flete en mano; nos depositará nuestra comisión ({{ getTierCommission(selectedTier) }}%) y conservará su parte.
                      </span>
                    </p>
                  </div>

                  <!-- Valor Producto COD -->
                  <div>
                    <label :class="labelClasses">Cobro Efectivo Producto (COD)</label>
                    <div class="relative">
                      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">C$</span>
                      <input
                        v-model.number="form.product_value_cod"
                        :disabled="form.payment_method === 'PAID_TO_BUSINESS'"
                        type="number"
                        step="0.01"
                        min="0"
                        :class="[inputClasses, 'pl-8 font-mono font-bold text-slate-900 dark:text-white']"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <!-- Tarjeta de Resumen Financiero Conectada a la BD de service_tiers -->
                  <div v-if="selectedTier" class="p-4 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 space-y-2.5 font-mono text-xs shadow-sm">
                    <div class="flex justify-between items-center text-slate-600 dark:text-slate-400">
                      <span>Distancia OSRM:</span>
                      <span class="font-bold text-slate-900 dark:text-white">{{ form.distance_km }} km ({{ form.duration_mins }} min)</span>
                    </div>

                    <div class="flex justify-between items-center text-slate-600 dark:text-slate-400">
                      <span>Tarifa Base (0 - {{ Number(selectedTier.min_distance_km) || 0 }} km):</span>
                      <span class="font-bold">C$ {{ formatMoney(selectedTier.base_fare) }}</span>
                    </div>

                    <div v-if="form.distance_km > (Number(selectedTier.min_distance_km) || 0)" class="flex justify-between items-center text-slate-600 dark:text-slate-400">
                      <span>KM Extras ({{ (form.distance_km - (Number(selectedTier.min_distance_km) || 0)).toFixed(2) }} km × C$ {{ formatMoney(selectedTier.price_per_km) }}):</span>
                      <span class="font-bold">+ C$ {{ formatMoney(extraKmCost) }}</span>
                    </div>

                    <div class="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between items-center text-sm font-bold text-slate-900 dark:text-white">
                      <span>FLETE TOTAL A COBRAR:</span>
                      <span class="text-base text-[#F36421]">C$ {{ formatMoney(form.total_delivery_fee) }}</span>
                    </div>

                    <!-- Reparto Según Porcentaje de la Tabla service_tiers -->
                    <div class="grid grid-cols-3 gap-1.5 pt-2 border-t border-slate-200 dark:border-slate-700 text-[10px] text-center">
                      <div class="bg-blue-50 dark:bg-blue-950/30 p-1.5 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div class="text-slate-500 font-sans">Chofer ({{ 100 - getTierCommission(selectedTier) }}%)</div>
                        <div class="font-bold text-blue-600">C$ {{ formatMoney(form.driver_cut) }}</div>
                      </div>
                      <div class="bg-orange-50 dark:bg-orange-950/30 p-1.5 rounded-lg border border-[#F36421]/30">
                        <div class="text-slate-500 font-sans">LLEVO ({{ getTierCommission(selectedTier) }}%)</div>
                        <div class="font-bold text-[#F36421]">C$ {{ formatMoney(form.agency_cut) }}</div>
                      </div>
                      <div class="bg-emerald-50 dark:bg-emerald-950/30 p-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800">
                        <div class="text-slate-500 font-sans">Agencia USD</div>
                        <div class="font-bold text-emerald-600">${{ formatMoney(form.agency_usd) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- =============================================================== -->
            <!-- COLUMNA DERECHA (7 COLS): MAPA LEAFLET SIEMPRE VISIBLE -->
            <!-- =============================================================== -->
            <div class="lg:col-span-7 flex flex-col">
              <div :class="[sectionCardClasses, 'p-0 overflow-hidden flex-1 min-h-[460px] flex flex-col relative']">
                <!-- Cabecera del Mapa -->
                <div class="px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                    <Navigation class="w-4 h-4 text-blue-600" />
                    Mapa Satelital de Despacho (Managua / Nicaragua)
                  </span>
                  <span v-if="isRouting" class="text-xs font-bold text-blue-600 flex items-center gap-1.5">
                    <Loader2 class="w-3.5 h-3.5 animate-spin" /> Trazando ruta...
                  </span>
                  <span v-else-if="form.distance_km > 0" class="text-xs font-mono font-bold text-emerald-600">
                    ✔ Ruta: {{ form.distance_km }} km
                  </span>
                </div>

                <!-- Contenedor del Mapa Leaflet -->
                <div id="order-leaflet-map" class="w-full flex-1 min-h-[420px] z-10"></div>

                <!-- Banner Flotante Informativo en Modo Edición de Pin -->
                <div
                  v-if="mapPickerMode"
                  class="absolute top-14 left-4 right-4 z-20 bg-[#142032]/95 backdrop-blur-md p-3.5 rounded-xl border border-slate-700 shadow-2xl flex items-center justify-between text-white animate-fade-in"
                >
                  <div class="flex items-center gap-2.5">
                    <span class="text-xl">{{ mapPickerMode === 'pickup' ? '🟢' : '🔴' }}</span>
                    <div>
                      <p class="text-[11px] font-bold uppercase tracking-wider text-[#F36421]">
                        Moviendo pin de {{ mapPickerMode === 'pickup' ? 'Origen (Pickup)' : 'Destino (Dropoff)' }}
                      </p>
                      <p class="text-[11px] text-slate-300">
                        Haz clic sobre cualquier calle del mapa para reposicionarlo al instante.
                      </p>
                    </div>
                  </div>
                  <button
                    @click="mapPickerMode = null"
                    type="button"
                    class="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition-colors"
                  >
                    Listo
                  </button>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>

      <!-- Pie de Modal Principal -->
      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <button
          type="button"
          @click="prevTab"
          :disabled="activeTab === 'pickup'"
          class="px-4 py-2.5 text-xs font-bold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          ← Paso Anterior
        </button>

        <div class="flex gap-3 w-full sm:w-auto justify-end ml-auto">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
          <button v-if="activeTab !== 'pricing'" @click="nextTab" type="button" :class="btnPrimary">Siguiente →</button>
          <button v-else type="submit" form="orderForm" :disabled="isSaving || form.distance_km === 0 || !form.service_tier_id" :class="btnPrimary">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Crear Orden & WhatsApp
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue';
import { Package, Store, MapPin, Navigation, Loader2, X, Truck, Save } from 'lucide-vue-next';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

// LEAFLET IMPORT
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const props = defineProps({ show: Boolean });
const emit = defineEmits(['close', 'saved']);

const { showSuccess, showError } = useToast();
const isSaving = ref(false);
const isRouting = ref(false);
const isLoadingAddresses = ref(false);

const exchangeRate = 36.62;

// TABS WIZARD
const activeTab = ref('pickup');
const tabs = [
  { id: 'pickup', name: '1. Origen (Pickup)' },
  { id: 'dropoff', name: '2. Destino (Dropoff)' },
  { id: 'pricing', name: '3. Tarifa, Pago & COD' }
];

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all text-slate-700 dark:text-slate-200 disabled:opacity-50 disabled:bg-slate-50";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800";
const sectionTitleClasses = "text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-tight";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-[#F36421] rounded-xl hover:bg-[#d95214] shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const clientsList = ref([]);
const clientAddresses = ref([]);
const tiersList = ref([]);
const selectedClient = ref(null);

const selectedPickupAddressId = ref('');
const selectedDropoffAddressId = ref('');

// Control del Mapa Único en la Columna Derecha
const mapPickerMode = ref(null);
let mapInstance = null;
let pickupMarker = null;
let dropoffMarker = null;
let routePolyline = null;

const originIcon = L.divIcon({
  className: 'custom-pin-origin',
  html: `<div style="font-size: 28px; line-height: 1; transform: translate(-50%, -100%);">🟢</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28]
});

const destinationIcon = L.divIcon({
  className: 'custom-pin-dest',
  html: `<div style="font-size: 28px; line-height: 1; transform: translate(-50%, -100%);">🔴</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28]
});

const form = reactive({
  client_id: '',
  service_tier_id: '',
  service_type: '',
  payment_method: 'CASH_ON_DELIVERY',
  pickup_lat: 12.148421,
  pickup_lng: -86.244974,
  pickup_address: '',
  dropoff_lat: 12.093710,
  dropoff_lng: -86.306877,
  dropoff_address: '',
  receiver_name: '',
  receiver_phone: '',
  distance_km: 0.00,
  duration_mins: 0,
  product_value_cod: 0.00,
  total_delivery_fee: 0.00,
  driver_cut: 0.00,
  agency_cut: 0.00,
  agency_usd: 0.00,
  route_geometry: ''
});

const nextTab = () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) {
    activeTab.value = tabs[currentIndex + 1].id;
  }
};

const prevTab = () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex > 0) {
    activeTab.value = tabs[currentIndex - 1].id;
  }
};

// ============================================================================
// 1. CARGA DE TARIFAS Y CÁLCULO ESTRICTO SEGÚN LA TABLA `service_tiers`
// ============================================================================
const loadServiceTiers = async () => {
  try {
    const res = await apiClient.get('/service_tiers');
    const allTiers = Array.isArray(res.data) ? res.data : (res.data.data || []);
    tiersList.value = allTiers.filter(t => t.status === 'ACTIVE' || !t.status);

    if (tiersList.value.length > 0 && !form.service_tier_id) {
      form.service_tier_id = tiersList.value[0].id;
    }
  } catch (e) {
    showError('No se pudo cargar el tarifario de servicios.');
  }
};

// Objeto de la tarifa activa según tu BD
const selectedTier = computed(() => {
  return tiersList.value.find(t => String(t.id) === String(form.service_tier_id)) || null;
});

// En tu tabla MySQL "service_tiers", el porcentaje de comisión se almacena en interest_rate
const getTierCommission = (tier) => {
  if (!tier) return 25;
  return Number(tier.commission_rate ?? tier.interest_rate ?? 25);
};

// Cálculo dinámico real según min_distance_km y price_per_km de tu tabla service_tiers
const extraKmCost = computed(() => {
  if (!selectedTier.value) return 0.00;
  const minKm = Number(selectedTier.value.min_distance_km) || 0;
  const kmPrice = Number(selectedTier.value.price_per_km) || 0;

  if (form.distance_km <= minKm) return 0.00;
  return (form.distance_km - minKm) * kmPrice;
});

const recalculatePricing = () => {
  if (!selectedTier.value) return;

  const baseFare = Number(selectedTier.value.base_fare) || 0;
  const total = Math.ceil(baseFare + extraKmCost.value);
  const commRate = getTierCommission(selectedTier.value) / 100;

  form.total_delivery_fee = total;
  form.agency_cut = Number((total * commRate).toFixed(2));
  form.driver_cut = Number((total - form.agency_cut).toFixed(2));
  form.agency_usd = Number((form.agency_cut / exchangeRate).toFixed(2));
};

// Si cambia la tarifa seleccionada o la distancia de OSRM, recalcular automáticamente
watch([selectedTier, () => form.distance_km], () => {
  if (selectedTier.value) {
    form.service_type = selectedTier.value.service_type || '';
    recalculatePricing();
  }
}, { immediate: true });

// ============================================================================
// 2. MAPA LEAFLET ÚNICO A LA DERECHA & RUTEO OSRM
// ============================================================================
const initMap = () => {
  nextTick(() => {
    const container = document.getElementById('order-leaflet-map');
    if (!container) return;

    if (!mapInstance) {
      mapInstance = L.map('order-leaflet-map').setView([12.136389, -86.251389], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(mapInstance);

      mapInstance.on('click', (e) => {
        const lat = Number(e.latlng.lat.toFixed(8));
        const lng = Number(e.latlng.lng.toFixed(8));

        if (mapPickerMode.value === 'pickup') {
          form.pickup_lat = lat;
          form.pickup_lng = lng;
          showSuccess('🟢 Origen (Pickup) actualizado en el mapa');
          calculateOSRMRoute();
        } else if (mapPickerMode.value === 'dropoff') {
          form.dropoff_lat = lat;
          form.dropoff_lng = lng;
          showSuccess('🔴 Destino (Dropoff) actualizado en el mapa');
          calculateOSRMRoute();
        }
      });
    }

    setTimeout(() => {
      mapInstance?.invalidateSize();
      calculateOSRMRoute();
    }, 250);
  });
};

const activateMapMode = (mode) => {
  mapPickerMode.value = mode;
  if (mapInstance) {
    const targetLat = mode === 'pickup' ? form.pickup_lat : form.dropoff_lat;
    const targetLng = mode === 'pickup' ? form.pickup_lng : form.dropoff_lng;
    mapInstance.setView([targetLat, targetLng], 15);
  }
};

const calculateOSRMRoute = async () => {
  if (!form.pickup_lat || !form.pickup_lng || !form.dropoff_lat || !form.dropoff_lng) return;

  isRouting.value = true;
  try {
    const osrmUrl = `https://routing.openstreetmap.de/routed-car/route/v1/driving/${form.pickup_lng},${form.pickup_lat};${form.dropoff_lng},${form.dropoff_lat}?overview=full&geometries=geojson`;
    const response = await fetch(osrmUrl);
    const data = await response.json();

    if (data.code === 'Ok' && data.routes?.length > 0) {
      const route = data.routes[0];
      form.distance_km = Number((route.distance / 1000).toFixed(2));
      form.duration_mins = Math.ceil(route.duration / 60);
      form.route_geometry = JSON.stringify(route.geometry.coordinates);
      recalculatePricing();
      drawRouteOnMap(route.geometry.coordinates);
    }
  } catch (error) {
    console.error('Error OSRM:', error);
  } finally {
    isRouting.value = false;
  }
};

const drawRouteOnMap = (geojsonCoords) => {
  if (!mapInstance) return;

  if (pickupMarker) mapInstance.removeLayer(pickupMarker);
  if (dropoffMarker) mapInstance.removeLayer(dropoffMarker);
  if (routePolyline) mapInstance.removeLayer(routePolyline);

  pickupMarker = L.marker([form.pickup_lat, form.pickup_lng], { icon: originIcon }).addTo(mapInstance);
  dropoffMarker = L.marker([form.dropoff_lat, form.dropoff_lng], { icon: destinationIcon }).addTo(mapInstance);

  const latLngs = geojsonCoords.map(coord => [coord[1], coord[0]]);
  routePolyline = L.polyline(latLngs, { color: '#0066FF', weight: 5, opacity: 0.85 }).addTo(mapInstance);
  mapInstance.fitBounds(routePolyline.getBounds(), { padding: [40, 40] });
};

// ============================================================================
// 3. CATÁLOGO DE CLIENTES Y SUCURSALES (`customer_addresses`)
// ============================================================================
const loadClients = async () => {
  try {
    const res = await apiClient.get('/customers');
    clientsList.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error al cargar catálogo de clientes.');
  }
};

const loadCustomerAddresses = async (customerId) => {
  isLoadingAddresses.value = true;
  try {
    const res = await apiClient.get(`/customer_addresses?customer_id=${customerId}`);
    clientAddresses.value = Array.isArray(res.data) ? res.data : (res.data.data || []);

    const defAddr = clientAddresses.value.find(a => a.is_default || a.is_default === 1) || clientAddresses.value[0];
    if (defAddr) {
      selectedPickupAddressId.value = defAddr.id;
      applyPickupAddress();
    }
  } catch (e) {
    console.error("Error cargando sucursales de customer_addresses:", e);
    clientAddresses.value = [];
  } finally {
    isLoadingAddresses.value = false;
  }
};

const onClientChange = async () => {
  selectedClient.value = clientsList.value.find(c => c.id === form.client_id) || null;
  selectedPickupAddressId.value = '';
  selectedDropoffAddressId.value = '';
  clientAddresses.value = [];

  if (selectedClient.value) {
    await loadCustomerAddresses(selectedClient.value.id);

    if (clientAddresses.value.length === 0 && selectedClient.value.gps_coordinates) {
      const [lat, lng] = selectedClient.value.gps_coordinates.split(',').map(n => Number(n.trim()));
      if (lat && lng) {
        form.pickup_lat = lat;
        form.pickup_lng = lng;
        form.pickup_address = selectedClient.value.address || '';
        calculateOSRMRoute();
      }
    }
  }
};

const applyPickupAddress = () => {
  if (!selectedPickupAddressId.value) return;
  const addr = clientAddresses.value.find(a => a.id === selectedPickupAddressId.value);
  if (addr) {
    form.pickup_lat = Number(addr.latitude) || form.pickup_lat;
    form.pickup_lng = Number(addr.longitude) || form.pickup_lng;
    form.pickup_address = `${addr.alias} — ${addr.address}` + (addr.reference_address ? ` (${addr.reference_address})` : '');
    calculateOSRMRoute();
  }
};

const applyDropoffAddress = () => {
  if (!selectedDropoffAddressId.value) return;
  const addr = clientAddresses.value.find(a => a.id === selectedDropoffAddressId.value);
  if (addr) {
    form.dropoff_lat = Number(addr.latitude) || form.dropoff_lat;
    form.dropoff_lng = Number(addr.longitude) || form.dropoff_lng;
    form.dropoff_address = `${addr.alias} — ${addr.address}` + (addr.reference_address ? ` (${addr.reference_address})` : '');
    form.receiver_phone = addr.contact_phone || form.receiver_phone;
    calculateOSRMRoute();
  }
};

// ============================================================================
// 4. GUARDAR ORDEN EN LA BD & ABRIR WHATSAPP
// ============================================================================
const handleSubmit = async () => {
  if (!form.client_id) return showError('Por favor selecciona un Comercio o Cliente (Paso 1).');
  if (!form.pickup_address) return showError('Falta la dirección de origen / recolección (Paso 1).');
  if (!form.dropoff_address) return showError('Falta la dirección de destino / entrega (Paso 2).');
  if (!form.service_tier_id) return showError('Por favor selecciona una Tarifa Logística (Paso 3).');

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const payload = {
      ...form,
      company_id: user?.company || 1,
      status: 'PENDING',
      tracking_code: `LLEVO-${Date.now().toString().slice(-6)}`,
      driver_commission_fee: Number(form.driver_cut) || 0.00,
      llevo_net_profit: Number(form.agency_cut) || 0.00,
      agency_usd: Number(form.agency_usd) || 0.00,
      base_fare: Number(selectedTier.value?.base_fare) || 0.00,
      km_fee: Number(extraKmCost.value) || 0.00,
      total_delivery_fee: Number(form.total_delivery_fee) || 0.00
    };

    await apiClient.post('/orders', payload);
    showSuccess('✔ Orden de envío guardada en LLEVO');

    const clientPhone = selectedClient.value?.mobile_phone?.replace(/\D/g, '') || '';
    const truck = '\u{1F69A}';
    const pin   = '\u{1F4CD}';
    const flag  = '\u{1F3C1}';
    const ruler = '\u{1F4D0}';
    const moto  = '\u{1F6F5}';
    const card  = '\u{1F4B3}';
    const money = '\u{1F4B0}';
    const cash  = '\u{1F4B5}';

    const textMessage =
      `${truck} *LLEVO | COTIZACIÓN DE DESPACHO*\n` +
      `-----------------------------------\n` +
      `${pin} *Origen:* ${form.pickup_address}\n` +
      `${flag} *Destino:* ${form.dropoff_address}\n` +
      `${ruler} *Distancia:* ${form.distance_km} km (${form.duration_mins} min)\n` +
      `${moto} *Servicio:* ${selectedTier.value?.name || form.service_type}\n` +
      `${card} *Pago:* ${form.payment_method === 'CASH_ON_DELIVERY' ? 'Efectivo en Entrega' : form.payment_method === 'PAID_TO_BUSINESS' ? 'Ya pagado al negocio' : 'Envío Directo / Prepago'}\n` +
      `-----------------------------------\n` +
      `${money} *Flete Total:* C$ ${form.total_delivery_fee}\n` +
      (form.product_value_cod > 0 && form.payment_method !== 'PAID_TO_BUSINESS' ? `${cash} *Efectivo COD:* C$ ${form.product_value_cod}\n` : '') +
      `\nEn breve te confirmaremos el motorizado asignado. ¡Gracias por confiar en LLEVO!`;

    if (clientPhone) {
      const params = new URLSearchParams({
        phone: `505${clientPhone}`,
        text: textMessage
      });
      window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError('No se pudo guardar la orden en la central.');
  } finally {
    isSaving.value = false;
  }
};

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
const getFleetIcon = (type) => {
  const icons = { 'MOTO_EXPRESS': '🛵', 'CARRO_CARGA': '🚗', 'CAMIONETA_B2B': '🛻' };
  return icons[type] || '📦';
};

watch(() => props.show, (isOpen) => {
  if (isOpen) {
    activeTab.value = 'pickup';
    loadClients();
    loadServiceTiers();
    initMap();
  }
});
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>